/**
 * エラーハンドリングユーティリティ
 * @description 統一されたエラー処理とログ記録を提供
 */

import i18n from '../i18n';

/**
 * バックエンドAPI統一エラーレスポンス形式
 */
interface BackendErrorResponse {
  success: false;
  error: {
    code: string;
    message: string;
    translationKey: string;
    details?: Record<string, unknown>;
  };
}

export enum ErrorSeverity {
  LOW = 'low',
  MEDIUM = 'medium',
  HIGH = 'high',
  CRITICAL = 'critical'
}

export enum ErrorCategory {
  NETWORK = 'network',
  VALIDATION = 'validation',
  AUTHENTICATION = 'authentication',
  AUTHORIZATION = 'authorization',
  BUSINESS_LOGIC = 'business_logic',
  SYSTEM = 'system',
  USER_INPUT = 'user_input'
}

export interface AppError {
  id: string;
  code: string;
  message: string;
  userMessage: string;
  category: ErrorCategory;
  severity: ErrorSeverity;
  timestamp: Date;
  context?: Record<string, any>;
  stack?: string;
  userId?: string;
  sessionId?: string;
}

export interface ErrorHandlerConfig {
  enableLogging: boolean;
  enableReporting: boolean;
  enableUserNotification: boolean;
  maxRetries: number;
  retryDelay: number;
}

/**
 * 翻訳キーからローカライズされたエラーメッセージを取得
 * バックエンドから返されるtranslationKeyを使用
 */
function getLocalizedErrorMessage(translationKey: string, fallbackMessage: string): string {
  // i18nが初期化されていない場合はフォールバック
  if (!i18n.isInitialized) {
    return fallbackMessage;
  }

  // 翻訳キーが存在する場合は翻訳を取得
  if (i18n.exists(translationKey)) {
    return i18n.t(translationKey);
  }

  // 翻訳キーが存在しない場合はフォールバックメッセージを使用
  return fallbackMessage;
}

class ErrorHandlerService {
  private config: ErrorHandlerConfig = {
    enableLogging: true,
    enableReporting: true,
    enableUserNotification: true,
    maxRetries: 3,
    retryDelay: 1000
  };

  private errorLog: AppError[] = [];
  private notificationCallbacks: ((error: AppError) => void)[] = [];

  /**
   * エラーを処理する
   */
  public handleError(
    error: Error | AppError | string,
    category: ErrorCategory = ErrorCategory.SYSTEM,
    severity: ErrorSeverity = ErrorSeverity.MEDIUM,
    context?: Record<string, any>
  ): AppError {
    const appError = this.normalizeError(error, category, severity, context);

    // バックエンドAPIエラーレスポンスからtranslationKeyを抽出
    if (context?.response) {
      const responseData = context.response;
      // レスポンスがBackendErrorResponse形式かチェック
      if (responseData && typeof responseData === 'object' && 'error' in responseData) {
        const backendError = responseData as BackendErrorResponse;
        if (backendError.error && backendError.error.translationKey) {
          appError.userMessage = getLocalizedErrorMessage(
            backendError.error.translationKey,
            backendError.error.message
          );
          appError.code = backendError.error.code;
        }
      }
    }

    // ログ記録
    if (this.config.enableLogging) {
      this.logError(appError);
    }

    // エラーレポート送信
    if (this.config.enableReporting && this.shouldReport(appError)) {
      this.reportError(appError);
    }

    // 購入者通知
    if (this.config.enableUserNotification && this.shouldNotifyUser(appError)) {
      this.notifyUser(appError);
    }

    return appError;
  }

  /**
   * ネットワークエラーを処理する
   */
  public handleNetworkError(
    error: Error,
    requestUrl?: string,
    requestData?: any
  ): AppError {
    // navigator.connectionはChromium系ブラウザのみの非標準API（Network Information API）
    const connection = 'connection' in navigator
      ? (navigator as Navigator & { connection?: { effectiveType?: string } }).connection
      : undefined;

    const context = {
      url: requestUrl,
      requestData,
      userAgent: navigator.userAgent,
      ...(connection?.effectiveType ? { connectionType: connection.effectiveType } : {})
    };

    const appError = this.normalizeError(
      error,
      ErrorCategory.NETWORK,
      ErrorSeverity.HIGH,
      context
    );

    // ネットワークエラー特有の処理
    appError.userMessage = this.getNetworkErrorMessage(error);

    return this.handleError(appError);
  }

  /**
   * バリデーションエラーを処理する
   */
  public handleValidationError(
    fieldName: string,
    value: any,
    validationRule: string,
    customMessage?: string
  ): AppError {
    const context = {
      fieldName,
      value: typeof value === 'string' ? value.substring(0, 100) : value,
      validationRule
    };

    const appError: AppError = {
      id: this.generateErrorId(),
      code: `VALIDATION_${validationRule.toUpperCase()}`,
      message: `Validation failed for field ${fieldName}: ${validationRule}`,
      userMessage: customMessage || this.getValidationErrorMessage(fieldName, validationRule),
      category: ErrorCategory.VALIDATION,
      severity: ErrorSeverity.LOW,
      timestamp: new Date(),
      context
    };

    return this.handleError(appError);
  }

  /**
   * 認証エラーを処理する
   */
  public handleAuthenticationError(error: Error, action?: string): AppError {
    const context = {
      action,
      currentPath: window.location.pathname,
      timestamp: new Date().toISOString()
    };

    const appError = this.normalizeError(
      error,
      ErrorCategory.AUTHENTICATION,
      ErrorSeverity.HIGH,
      context
    );

    appError.userMessage = getLocalizedErrorMessage(
      'error.auth_session_expired',
      'ログインが必要です。再度ログインしてください。'
    );

    // 認証エラー時の自動リダイレクト
    if (action === 'auto_redirect') {
      setTimeout(() => {
        window.location.href = '/login';
      }, 2000);
    }

    return this.handleError(appError);
  }

  /**
   * 認可エラーを処理する
   */
  public handleAuthorizationError(
    requiredRole: string,
    currentRole?: string,
    resource?: string
  ): AppError {
    const context = {
      requiredRole,
      currentRole,
      resource,
      currentPath: window.location.pathname
    };

    const appError: AppError = {
      id: this.generateErrorId(),
      code: 'AUTHORIZATION_INSUFFICIENT_PRIVILEGES',
      message: `Access denied: Required role ${requiredRole}, current role ${currentRole}`,
      userMessage: getLocalizedErrorMessage(
        'error.auth_permission_denied',
        'この操作を実行する権限がありません。'
      ),
      category: ErrorCategory.AUTHORIZATION,
      severity: ErrorSeverity.HIGH,
      timestamp: new Date(),
      context
    };

    return this.handleError(appError);
  }

  /**
   * 非同期操作でのエラーリトライ
   */
  public async withRetry<T>(
    operation: () => Promise<T>,
    maxRetries: number = this.config.maxRetries,
    retryDelay: number = this.config.retryDelay,
    shouldRetry?: (error: Error) => boolean
  ): Promise<T> {
    let lastError: Error;
    
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        return await operation();
      } catch (error) {
        lastError = error as Error;
        
        // リトライするかどうかの判定
        if (attempt === maxRetries || (shouldRetry && !shouldRetry(lastError))) {
          break;
        }

        // リトライ間隔の計算（指数バックオフ）
        const delay = retryDelay * Math.pow(2, attempt - 1);
        await new Promise(resolve => setTimeout(resolve, delay));

        console.warn(`Operation failed, retrying (${attempt}/${maxRetries})`, {
          error: lastError.message,
          delay
        });
      }
    }
    
    // 全てのリトライが失敗した場合
    const appError = this.handleError(
      lastError!,
      ErrorCategory.SYSTEM,
      ErrorSeverity.HIGH,
      { retryAttempts: maxRetries }
    );
    
    throw appError;
  }

  /**
   * エラー通知コールバックを登録
   */
  public onError(callback: (error: AppError) => void): void {
    this.notificationCallbacks.push(callback);
  }

  /**
   * エラー通知コールバックを解除
   */
  public offError(callback: (error: AppError) => void): void {
    const index = this.notificationCallbacks.indexOf(callback);
    if (index > -1) {
      this.notificationCallbacks.splice(index, 1);
    }
  }

  /**
   * エラーログを取得
   */
  public getErrorLog(): AppError[] {
    return [...this.errorLog];
  }

  /**
   * エラーログをクリア
   */
  public clearErrorLog(): void {
    this.errorLog = [];
  }

  /**
   * 設定を更新
   */
  public updateConfig(config: Partial<ErrorHandlerConfig>): void {
    this.config = { ...this.config, ...config };
  }

  // プライベートメソッド

  private normalizeError(
    error: Error | AppError | string,
    category: ErrorCategory,
    severity: ErrorSeverity,
    context?: Record<string, any>
  ): AppError {
    if (typeof error === 'string') {
      return {
        id: this.generateErrorId(),
        code: 'GENERIC_ERROR',
        message: error,
        userMessage: error,
        category,
        severity,
        timestamp: new Date(),
        context
      };
    }

    if ('id' in error && 'code' in error) {
      // 既にAppErrorの場合
      return error;
    }

    // Error オブジェクトの場合
    return {
      id: this.generateErrorId(),
      code: error.name || 'UNKNOWN_ERROR',
      message: error.message,
      userMessage: this.getUserMessage(error, category),
      category,
      severity,
      timestamp: new Date(),
      context,
      stack: error.stack
    };
  }

  private generateErrorId(): string {
    return `err_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  private getUserMessage(_: Error, category: ErrorCategory): string {
    // カテゴリー別の購入者メッセージ生成
    switch (category) {
      case ErrorCategory.NETWORK:
        return getLocalizedErrorMessage(
          'error.network_error',
          'ネットワークエラーが発生しました。接続を確認してください。'
        );
      case ErrorCategory.VALIDATION:
        return getLocalizedErrorMessage(
          'error.validation_error',
          '入力内容に問題があります。'
        );
      case ErrorCategory.AUTHENTICATION:
        return getLocalizedErrorMessage(
          'error.auth_required',
          'ログインが必要です。'
        );
      case ErrorCategory.AUTHORIZATION:
        return getLocalizedErrorMessage(
          'error.auth_permission_denied',
          'この操作を実行する権限がありません。'
        );
      case ErrorCategory.BUSINESS_LOGIC:
        return getLocalizedErrorMessage(
          'error.business_logic_error',
          '処理中にエラーが発生しました。'
        );
      default:
        return getLocalizedErrorMessage(
          'error.system_error',
          'システムエラーが発生しました。'
        );
    }
  }

  private getNetworkErrorMessage(error: Error): string {
    // ネットワークエラーメッセージの詳細化
    if (error.message.includes('fetch')) {
      return getLocalizedErrorMessage(
        'error.network_fetch_failed',
        'サーバーとの通信に失敗しました。インターネット接続を確認してください。'
      );
    }
    if (error.message.includes('timeout')) {
      return getLocalizedErrorMessage(
        'error.network_timeout',
        'サーバーからの応答がありません。しばらく待ってから再試行してください。'
      );
    }
    if (error.message.includes('404')) {
      return getLocalizedErrorMessage(
        'error.network_not_found',
        '要求されたページまたはデータが見つかりません。'
      );
    }
    if (error.message.includes('500')) {
      return getLocalizedErrorMessage(
        'error.network_server_error',
        'サーバーエラーが発生しました。管理者にお問い合わせください。'
      );
    }
    return getLocalizedErrorMessage(
      'error.network_error',
      'ネットワークエラーが発生しました。'
    );
  }

  private getValidationErrorMessage(fieldName: string, rule: string): string {
    // バリデーションエラーメッセージの詳細化
    const fieldDisplayName = this.getFieldDisplayName(fieldName);

    switch (rule) {
      case 'required':
        return getLocalizedErrorMessage(
          'error.validation_required',
          `${fieldDisplayName}は必須です。`
        );
      case 'email':
        return getLocalizedErrorMessage(
          'error.validation_email_format',
          `${fieldDisplayName}の形式が正しくありません。`
        );
      case 'minLength':
        return getLocalizedErrorMessage(
          'error.validation_min_length',
          `${fieldDisplayName}は指定された文字数以上で入力してください。`
        );
      case 'maxLength':
        return getLocalizedErrorMessage(
          'error.validation_max_length',
          `${fieldDisplayName}は指定された文字数以下で入力してください。`
        );
      case 'pattern':
        return getLocalizedErrorMessage(
          'error.validation_pattern',
          `${fieldDisplayName}の形式が正しくありません。`
        );
      case 'min':
        return getLocalizedErrorMessage(
          'error.validation_min_value',
          `${fieldDisplayName}は指定された値以上で入力してください。`
        );
      case 'max':
        return getLocalizedErrorMessage(
          'error.validation_max_value',
          `${fieldDisplayName}は指定された値以下で入力してください。`
        );
      default:
        return getLocalizedErrorMessage(
          'error.validation_invalid_input',
          `${fieldDisplayName}の入力内容に問題があります。`
        );
    }
  }

  private getFieldDisplayName(fieldName: string): string {
    // フィールド名の日本語マッピング
    const fieldNames: Record<string, string> = {
      email: 'メールアドレス',
      password: 'パスワード',
      firstName: '名',
      lastName: '姓',
      phoneNumber: '電話番号',
      postalCode: '郵便番号',
      address: '住所',
      productName: '商品名',
      productPrice: '価格',
      productDescription: '商品説明',
      companyName: '会社名',
      bankAccount: '銀行口座',
      taxId: '税務登録番号'
    };
    
    return fieldNames[fieldName] || fieldName;
  }

  private logError(error: AppError): void {
    // エラーログに追加
    this.errorLog.push(error);
    
    // ログ数の制限
    if (this.errorLog.length > 1000) {
      this.errorLog = this.errorLog.slice(-500); // 最新500件を保持
    }
    
    // コンソールログ
    const logLevel = this.getLogLevel(error.severity);
    console[logLevel](`[${error.category}] ${error.message}`, {
      id: error.id,
      code: error.code,
      context: error.context,
      stack: error.stack
    });
  }

  private getLogLevel(severity: ErrorSeverity): 'log' | 'warn' | 'error' {
    switch (severity) {
      case ErrorSeverity.LOW:
        return 'log';
      case ErrorSeverity.MEDIUM:
        return 'warn';
      case ErrorSeverity.HIGH:
      case ErrorSeverity.CRITICAL:
        return 'error';
      default:
        return 'log';
    }
  }

  private shouldReport(error: AppError): boolean {
    // エラーレポート送信判定
    return error.severity === ErrorSeverity.HIGH ||
           error.severity === ErrorSeverity.CRITICAL;
  }

  private shouldNotifyUser(error: AppError): boolean {
    // 購入者通知判定
    return error.category !== ErrorCategory.SYSTEM ||
           error.severity === ErrorSeverity.CRITICAL;
  }

  private async reportError(error: AppError): Promise<void> {
    try {
      console.warn('Sending error report', {
        errorId: error.id,
        code: error.code,
        category: error.category,
        severity: error.severity
      });

      // TODO: 実際のAPIコールを実装
      // await fetch('/api/errors/report', {
      //   method: 'POST',
      //   headers: { 'Content-Type': 'application/json' },
      //   body: JSON.stringify(error)
      // });
    } catch (reportError) {
      console.error('Failed to report error:', reportError);
    }
  }

  private notifyUser(error: AppError): void {
    // 登録されたコールバックを実行
    this.notificationCallbacks.forEach(callback => {
      try {
        callback(error);
      } catch (callbackError) {
        console.error('Error in notification callback:', callbackError);
      }
    });
  }
}

// シングルトンインスタンス
export const errorHandler = new ErrorHandlerService();

// React Hook for Error Handling
export const useErrorHandler = () => {
  const handleError = (
    error: Error | string,
    category: ErrorCategory = ErrorCategory.SYSTEM,
    severity: ErrorSeverity = ErrorSeverity.MEDIUM,
    context?: Record<string, any>
  ) => {
    return errorHandler.handleError(error, category, severity, context);
  };

  const handleNetworkError = (error: Error, url?: string, data?: any) => {
    return errorHandler.handleNetworkError(error, url, data);
  };

  const handleValidationError = (
    fieldName: string,
    value: any,
    rule: string,
    customMessage?: string
  ) => {
    return errorHandler.handleValidationError(fieldName, value, rule, customMessage);
  };

  const withRetry = <T>(
    operation: () => Promise<T>,
    maxRetries?: number,
    retryDelay?: number,
    shouldRetry?: (error: Error) => boolean
  ) => {
    return errorHandler.withRetry(operation, maxRetries, retryDelay, shouldRetry);
  };

  return {
    handleError,
    handleNetworkError,
    handleValidationError,
    withRetry,
    onError: errorHandler.onError.bind(errorHandler),
    offError: errorHandler.offError.bind(errorHandler)
  };
};

// Error Boundary用のエラーハンドラー
export const handleErrorBoundaryError = (error: Error, errorInfo: any) => {
  return errorHandler.handleError(
    error,
    ErrorCategory.SYSTEM,
    ErrorSeverity.CRITICAL,
    {
      componentStack: errorInfo.componentStack,
      errorBoundary: true
    }
  );
};

export default errorHandler;
